import { noop } from "ts-essentials";

import { CumulativeIcon, LinearIcon } from "@hightouch/htui";
import { AggregationOption } from "src/pages/metrics/constants";
import { AnalyticsFrequency, IntervalUnit } from "src/types/visual";
import { DEFAULT_EXPERIMENT_MEASUREMENT_WINDOW } from "@hightouch/lib/query/visual/types/attribution-window";
import { v4 as uuidv4 } from "uuid";

import {
  AnalyticsActions,
  AnalyticsState,
  ChartFormState,
  ChartSchema,
  ChartTab,
  ConversionWindow,
  ExperimentsFormState,
  FunnelMeasurementType,
  FunnelStep,
  MeasurementScope,
  MetricSelection,
  SelectedAudience,
  TimeOptions,
} from "src/pages/analytics/types";
import {
  GraphType,
  MeasuringMode,
  GroupByColumn,
} from "@hightouch/lib/query/visual/types";
import isEqual from "lodash/isEqual";

export const SECONDS_IN_ONE_DAY = 86400;

export const TimeMap: Record<
  | TimeOptions.SevenDays
  | TimeOptions.FourteenDays
  | TimeOptions.ThirtyDays
  | TimeOptions.SixtyDays
  | TimeOptions.NinetyDays,
  number
> = {
  [TimeOptions.SevenDays]: SECONDS_IN_ONE_DAY * 7,
  [TimeOptions.FourteenDays]: SECONDS_IN_ONE_DAY * 14,
  [TimeOptions.ThirtyDays]: SECONDS_IN_ONE_DAY * 30,
  [TimeOptions.SixtyDays]: SECONDS_IN_ONE_DAY * 60,
  [TimeOptions.NinetyDays]: SECONDS_IN_ONE_DAY * 90,
};

export const SecondsToTimeOptions = {
  [SECONDS_IN_ONE_DAY * 7]: TimeOptions.SevenDays,
  [SECONDS_IN_ONE_DAY * 14]: TimeOptions.FourteenDays,
  [SECONDS_IN_ONE_DAY * 30]: TimeOptions.ThirtyDays,
  [SECONDS_IN_ONE_DAY * 60]: TimeOptions.SixtyDays,
  [SECONDS_IN_ONE_DAY * 90]: TimeOptions.NinetyDays,
};

export const LookbackOptions = [
  { label: "7d", value: TimeOptions.SevenDays },
  { label: "30d", value: TimeOptions.ThirtyDays },
  { label: "60d", value: TimeOptions.SixtyDays },
  { label: "90d", value: TimeOptions.NinetyDays },
];

export const TimeOptionsToDays = {
  [TimeOptions.SevenDays]: 7,
  [TimeOptions.FourteenDays]: 14,
  [TimeOptions.ThirtyDays]: 30,
  [TimeOptions.SixtyDays]: 60,
  [TimeOptions.NinetyDays]: 90,
};

export const AccumulationTypes = [
  {
    label: "Over time",
    description: "Metric is charted over time",
    value: false,
    icon: LinearIcon,
  },
  {
    label: "Cumulative",
    description: "Running total of your metric",
    value: true,
    icon: CumulativeIcon,
  },
];

export const RollupFrequencies = [
  { label: "Daily", value: AnalyticsFrequency.Daily },
  { label: "Weekly", value: AnalyticsFrequency.Weekly },
  { label: "Monthly", value: AnalyticsFrequency.Monthly },
];

// We want to express null but the options value can't be null so will use an
// empty string to represent null
export const NULL_OPTION_VALUE = "";

export const STATIC_ARRAY = [];
export const PLACEHOLDER_AUDIENCE: SelectedAudience = {
  id: -1,
  name: NULL_OPTION_VALUE,
  splits: [],
};
export const PLACEHOLDER_GROUP_BY: GroupByColumn = {
  type: "raw",
  modelId: NULL_OPTION_VALUE,
  name: NULL_OPTION_VALUE,
};
export const DEFAULT_METRIC_SELECTION: MetricSelection[] = [
  {
    id: NULL_OPTION_VALUE,
    name: NULL_OPTION_VALUE,
    eventModelId: null,
    description: null,
    aggregationMethod: AggregationOption.Count,
    conditions: [],
  },
];
export const isDefaultMetricSelection = (metricSelection: MetricSelection) =>
  isEqual(metricSelection, DEFAULT_METRIC_SELECTION[0]);

export const getDefaultFunnelSteps: () => FunnelStep[] = () => [
  { id: uuidv4(), eventModelId: "", relationshipId: "", subconditions: [] },
  { id: uuidv4(), eventModelId: "", relationshipId: "", subconditions: [] },
];

export const DEFAULT_SELECTED_AUDIENCES: SelectedAudience[] = [];
export const DEFAULT_GROUP_BY_COLUMNS: GroupByColumn[] = [];
export const DEFAULT_SELECTED_DATES: string[] = [];

export const DEFAULT_FUNNEL_CONVERSION_WINDOW: ConversionWindow = {
  quantity: 7,
  unit: IntervalUnit.Day,
};

export const defaultSchemaState: ChartSchema = {
  audiences: STATIC_ARRAY,
  dependenciesLoading: false,
  events: STATIC_ARRAY,
  metrics: STATIC_ARRAY,
  traits: STATIC_ARRAY,
  relatedModels: STATIC_ARRAY,
  parent: null,
  parentModelLoading: false,
  parentModelOptions: STATIC_ARRAY,
  parentModelOptionsLoading: false,
  measurementsOptions: STATIC_ARRAY,
  measurementsOptionsLoading: false,
};

// Re-exported so the app and the analytics/scheduler backend read one definition
// — the two used to be hand-synced copies.
export { DEFAULT_EXPERIMENT_MEASUREMENT_WINDOW };

export const defaultFormState: ChartFormState = {
  parentModelId: null,
  measuringSelection: {
    id: "",
    scope: MeasurementScope.AllData,
  },
  cumulative: false,
  funnelConversionWindow: DEFAULT_FUNNEL_CONVERSION_WINDOW,
  funnelSteps: getDefaultFunnelSteps(),
  chartTab: ChartTab.Insights,
  graphType: GraphType.Line,
  groupByColumns: DEFAULT_GROUP_BY_COLUMNS,
  funnelMeasuringType: FunnelMeasurementType.TotalConversion,
  metricSelection: DEFAULT_METRIC_SELECTION,
  rollupFrequency: AnalyticsFrequency.Weekly,
  selectedAudiences: DEFAULT_SELECTED_AUDIENCES,
  selectedDates: [],
  timeValue: TimeOptions.ThirtyDays,
  measuringMode: undefined,

  // New fields for experiments
  cohorts: undefined,
  globalCohortFilter: undefined,
  globalMeasurementWindow: undefined,
  globalNormalization: undefined,
};

export const defaultState: AnalyticsState = {
  ...defaultSchemaState,
  ...defaultFormState,
};

export const defaultActions: AnalyticsActions = {
  addGroupByColumn: noop,
  addGroupByColumns: noop,
  removeGroupByColumns: noop,
  resetView: noop,
  selectParentModel: noop,
  applySavedParentModel: noop,
  setLookbackWindow: noop,
  setSelectedDates: noop,
  selectMeasurement: noop,
  selectMeasurementMode: noop,
  setChartTab: noop,
  setGlobalMeasurementWindow: noop,
  setGlobalCohortFilter: noop,
  setGlobalNormalization: noop,
};

export const defaultInsightsChartState = {
  cumulative: false,
  rollupFrequency: AnalyticsFrequency.Weekly,
  groupByColumns: DEFAULT_GROUP_BY_COLUMNS,
  metricSelection: DEFAULT_METRIC_SELECTION,
  selectedDates: DEFAULT_SELECTED_DATES,
  timeValue: TimeOptions.ThirtyDays,
  measuringMode: undefined,
  globalCohortFilter: undefined,
  globalMeasurementWindow: undefined,
};

export const defaultFunnelChartState = {
  funnelConversionWindow: DEFAULT_FUNNEL_CONVERSION_WINDOW,
  funnelSteps: getDefaultFunnelSteps(),
  groupByColumns: DEFAULT_GROUP_BY_COLUMNS,
  funnelMeasuringType: FunnelMeasurementType.TotalConversion,
  selectedDates: DEFAULT_SELECTED_DATES,
  timeValue: TimeOptions.SevenDays,
  measuringMode: undefined,
  globalCohortFilter: undefined,
  globalMeasurementWindow: undefined,
};

export const defaultExperimentsFormState: ExperimentsFormState &
  Pick<ChartFormState, "measuringMode"> = {
  cohorts: undefined,
  globalCohortFilter: undefined,
  globalMeasurementWindow: DEFAULT_EXPERIMENT_MEASUREMENT_WINDOW,
  globalNormalization: undefined,

  // Default measurement mode for experiments
  measuringMode: MeasuringMode.SplitsMeasurement,
};
